import React, { useState } from "react";
import {
  ArrowLeft,
  ClipboardList,
  Send,
  FileCheck2,
  Sparkles,
  HelpCircle,
  UserCircle,
} from "lucide-react";
import { Button } from "./ui/button";
import ProductRequestForm from "./pricerequests/ProductRequestForm";
import RequestHistory from "./pricerequests/RequestHistory";

const STEPS = [
  { key: "products", label: "Add products", icon: ClipboardList },
  { key: "submit", label: "Submit", icon: Send },
  { key: "track", label: "Track quote", icon: FileCheck2 },
];

const StepIndicator = ({ currentStep }) => {
  const currentIdx = STEPS.findIndex((s) => s.key === currentStep);
  return (
    <ol
      className="flex flex-wrap items-center gap-2 sm:gap-3"
      aria-label="Quotation workflow steps"
      data-testid="pr-step-indicator"
    >
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <li key={s.key} className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
                active
                  ? "bg-cyan-600 text-white border-cyan-600"
                  : done
                  ? "bg-green-100 text-green-800 border-green-300"
                  : "bg-white text-slate-600 border-slate-300"
              }`}
              aria-current={active ? "step" : undefined}
            >
              <Icon className="w-3.5 h-3.5" aria-hidden="true" />
              {i + 1}. {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <span className="hidden sm:inline text-slate-300" aria-hidden="true">
                →
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const HelpCard = () => (
  <aside
    className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-3"
    data-testid="pr-help-card"
    aria-label="Quotation tips"
  >
    <div className="flex items-center gap-2 text-[#1E3A5F] font-bold">
      <HelpCircle className="w-5 h-5" aria-hidden="true" />
      Tips for the best quote
    </div>
    <ul className="text-sm text-slate-700 space-y-2 list-disc list-inside">
      <li>Add clear product photos for more accurate quotations.</li>
      <li>Include brand, model, reference, and quantity when available.</li>
      <li>Your quotation PDF will appear here when ready.</li>
    </ul>
    <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3 text-xs text-cyan-900 flex gap-2">
      <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <span>
        Sourcing usually starts within a few minutes. You'll get a notification here
        once the quotation PDF is ready.
      </span>
    </div>
  </aside>
);

const AccountCard = ({ user }) => (
  <aside
    className="rounded-2xl border border-slate-200 bg-white p-5 space-y-1"
    data-testid="pr-account-card"
    aria-label="Signed-in account"
  >
    <div className="flex items-center gap-2 text-[#1E3A5F] font-bold">
      <UserCircle className="w-5 h-5" aria-hidden="true" />
      Signed in as
    </div>
    <div className="text-sm text-slate-800 font-semibold">{user?.username || "—"}</div>
    {user?.email && (
      <div className="text-xs text-slate-500 break-all">{user.email}</div>
    )}
    {user?.company && (
      <div className="text-xs text-slate-500">{user.company}</div>
    )}
    <p className="text-xs text-slate-500 pt-2">
      All quotation requests are linked to this account.
    </p>
  </aside>
);

const PriceRequestPage = ({ user, onNavigateBack }) => {
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const currentStep = "products";

  const handleSubmitted = () => {
    // Refresh history immediately after a successful submission.
    setHistoryRefreshKey((n) => n + 1);
  };

  return (
    <div className="min-h-screen bg-slate-50 page-transition">
      {/* Header */}
      <div className="bg-white border-b-2 border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <img
              src="https://customer-assets.emergentagent.com/job_dc1c0b21-5011-47e1-a19d-11de6678deed/artifacts/55cbyjpt_Logo.png"
              alt="PriceValuator Logo"
              className="h-24 sm:h-32 object-contain"
            />
            {onNavigateBack && (
              <Button
                variant="ghost"
                onClick={onNavigateBack}
                data-testid="pr-back-btn"
                className="text-[#1E3A5F] hover:bg-gray-100 rounded-full px-4 py-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to dashboard
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {/* Hero */}
        <section
          className="rounded-2xl border border-slate-200 bg-gradient-to-br from-cyan-50 via-white to-blue-50 p-6 sm:p-8 shadow-sm"
          data-testid="pr-hero"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-600 text-white text-xs font-semibold px-3 py-1 mb-3">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            PriceValuator quotations
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1E3A5F]">
            Request a Product Quotation
          </h1>
          <p className="mt-3 max-w-3xl text-slate-700 leading-relaxed">
            Add the products you need priced, attach photos when possible, and submit. Our
            sourcing team takes it from there — you'll see the request number below and the
            quotation PDF as soon as it's ready.
          </p>
          <div className="mt-5">
            <StepIndicator currentStep={currentStep} />
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            <div
              className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
              data-testid="pr-form-section"
            >
              <ProductRequestForm user={user} onSubmitted={handleSubmitted} />
            </div>

            <RequestHistory
              user={user}
              refreshKey={historyRefreshKey}
            />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <AccountCard user={user} />
            <HelpCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceRequestPage;
